'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import unescapedText from 'I18n/utils/unescapedText';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import UISelect from 'UIComponents/input/UISelect';
import UITextInput from 'UIComponents/input/UITextInput';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import CrmObject from 'ContentComponents/cmv2/fields/CrmObject';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { CRM_OBJECT } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DEFAULT, DISPLAY_FORMAT, DISPLAY_PROPERTIES, OBJECT_TYPE, PROPERTIES_TO_FETCH, PLACEHOLDER } from 'DesignData/enums/CustomWidgetFieldAttributes';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import withDebouncedInput from 'DesignEditorsUI/components/shared/inputs/withDebouncedInput';
import * as CrmObjectsActions from 'ContentData/actions/CrmObjects';
import * as crmObjectsSelectors from 'ContentData/selectors/crmObjects';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

const DebouncedUIExpandingTextArea = withDebouncedInput(UIExpandingTextArea);
const DebouncedUITextInput = withDebouncedInput(UITextInput);

function asMutable(list) {
  if (!list) return [];
  return list.asMutable ? list.asMutable() : list;
}

function makeMapStateToProps() {
  return (state, props) => {
    const fieldNamePath = customWidgetEditorOptionsSelectors.getSelectedFieldBreadcrumbs(state, props).map(field => field.name).join('.');
    const objectType = props.field[OBJECT_TYPE];
    return {
      crmPropertyOptions: crmObjectsSelectors.getCrmObjectPropertyOptions(state, {
        objectType
      }),
      crmObjectTypeOptions: crmObjectsSelectors.getCrmObjectTypeOptions(state),
      crmObjectSchema: crmObjectsSelectors.getCrmObjectSchema(state, {
        objectType
      }),
      fieldNamePath: `module.${fieldNamePath || props.field.name}`,
      selectedCrmObjectType: crmObjectsSelectors.getSelectedCrmObjectType(state, {
        objectType
      })
    };
  };
}

const mapDispatchToProps = {
  fetchCrmObjectSchema: CrmObjectsActions.fetchCrmObjectSchema,
  fetchCrmObjectTypes: CrmObjectsActions.fetchCrmObjectTypes
};

class CrmObjectContentOptions extends Component {
  constructor(...args) {
    super(...args);

    this.onObjectTypeChange = e => {
      this.props.editField({
        [OBJECT_TYPE]: e.target.value,
        [PROPERTIES_TO_FETCH]: [],
        [DISPLAY_PROPERTIES]: [],
        [DEFAULT]: null
      });
    };

    this.onPropertiesToFetchChange = e => {
      this.props.editField({
        [PROPERTIES_TO_FETCH]: e.target.value
      });
    };

    this.onDisplayPropertiesChange = e => {
      this.props.editField({
        [DISPLAY_PROPERTIES]: e.target.value
      });
    };

    this.onDisplayFormatChange = e => {
      this.props.editField({
        [DISPLAY_FORMAT]: e.target.value
      });
    };

    this.onPlaceholderChange = e => {
      this.props.editField({
        [PLACEHOLDER]: e.target.value
      });
    };

    this.onDefaultChange = value => {
      this.props.editField({
        [DEFAULT]: value
      });
    };
  }

  componentDidMount() {
    const {
      crmObjectSchema,
      fetchCrmObjectSchema,
      fetchCrmObjectTypes,
      field
    } = this.props;
    fetchCrmObjectTypes();
    const objectType = field[OBJECT_TYPE];

    if (objectType && !crmObjectSchema) {
      fetchCrmObjectSchema(objectType);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      crmObjectSchema,
      fetchCrmObjectSchema,
      field
    } = this.props;
    const objectType = field[OBJECT_TYPE];

    if (prevProps.field[OBJECT_TYPE] !== objectType && objectType && !crmObjectSchema) {
      fetchCrmObjectSchema(objectType);
    }
  }

  renderCRMObjectTypeSelect() {
    const {
      crmObjectTypeOptions,
      fieldErrors,
      readOnly,
      selectedCrmObjectType
    } = this.props;
    return /*#__PURE__*/_jsx(FormControlWithErrors, {
      errors: fieldErrors[OBJECT_TYPE],
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('crmobject.objectType.label')
      }),
      children: /*#__PURE__*/_jsx(SidebarInput, {
        inputComponent: UISelect,
        disabled: readOnly,
        onChange: this.onObjectTypeChange,
        options: crmObjectTypeOptions,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('crmobject.objectType.placeholder')
        }),
        required: true,
        value: selectedCrmObjectType
      })
    });
  }

  renderPropertiesToFetch() {
    const {
      crmPropertyOptions,
      field,
      fieldNamePath,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('crmobject.propertiesToFetch.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: i18nKey('crmobject.propertiesToFetch.tooltip'),
        options: {
          fieldNamePath
        }
      }),
      tooltipProps: {
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        inputComponent: UISelect,
        disabled: readOnly,
        onChange: this.onPropertiesToFetchChange,
        multi: true,
        options: crmPropertyOptions,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('crmobject.propertiesToFetch.placeholder')
        }),
        required: true,
        value: asMutable(field[PROPERTIES_TO_FETCH])
      })
    });
  }

  renderDisplayProperties() {
    const {
      crmPropertyOptions,
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('crmobject.displayProperties.label')
      }),
      tooltip: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('crmobject.displayProperties.tooltip1')
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('crmobject.displayProperties.tooltip2')
          })
        })]
      }),
      tooltipProps: {
        headingText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('crmobject.displayProperties.tooltipTitle')
        }),
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        inputComponent: UISelect,
        disabled: readOnly,
        onChange: this.onDisplayPropertiesChange,
        multi: true,
        options: crmPropertyOptions,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('crmobject.displayProperties.placeholder')
        }),
        required: true,
        value: asMutable(field[DISPLAY_PROPERTIES])
      })
    });
  }

  renderPropertiesDisplayFormat() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('crmobject.displayFormat.label')
      }),
      tooltip: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('crmobject.displayFormat.tooltip1')
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('crmobject.displayFormat.tooltip2')
          })
        })]
      }),
      tooltipProps: {
        headingText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('crmobject.displayFormat.tooltipTitle')
        }),
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        disabled: readOnly || !field[DISPLAY_PROPERTIES],
        inputComponent: DebouncedUIExpandingTextArea,
        maxHeight: 300,
        onChange: this.onDisplayFormatChange,
        placeholder: unescapedText(i18nKey('hubdbrow.displayFormat.placeholder')),
        required: true,
        shrink: true,
        value: field[DISPLAY_FORMAT]
      })
    });
  }

  renderPlaceholderInput() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('selectFields.placeholder.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('selectFields.placeholder.tooltip')
      }),
      children: /*#__PURE__*/_jsx(SidebarInput, {
        inputComponent: DebouncedUITextInput,
        onChange: this.onPlaceholderChange,
        value: field[PLACEHOLDER],
        disabled: readOnly
      })
    });
  }

  render() {
    const {
      crmObjectSchema,
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderCRMObjectTypeSelect(), field[OBJECT_TYPE] && crmObjectSchema && this.renderPropertiesToFetch(), field[OBJECT_TYPE] && crmObjectSchema && this.renderDisplayProperties(), field[OBJECT_TYPE] && crmObjectSchema && this.renderPropertiesDisplayFormat(), field[OBJECT_TYPE] && this.renderPlaceholderInput(), field[OBJECT_TYPE] && /*#__PURE__*/_jsx(ToolFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`defaultLabels.${CRM_OBJECT}`)
        }),
        children: /*#__PURE__*/_jsx(SidebarInput, {
          display_properties: field[DISPLAY_PROPERTIES],
          display_format: field[DISPLAY_FORMAT],
          inputComponent: CrmObject,
          object_type: field[OBJECT_TYPE],
          onFieldChange: this.onDefaultChange,
          placeholder: field[PLACEHOLDER],
          readOnly: readOnly,
          type: field.type,
          value: field[DEFAULT]
        })
      })]
    });
  }

}

CrmObjectContentOptions.propTypes = {
  crmObjectSchema: PropTypes.object,
  crmObjectTypeOptions: PropTypes.array.isRequired,
  crmPropertyOptions: PropTypes.array.isRequired,
  editField: PropTypes.func.isRequired,
  fetchCrmObjectSchema: PropTypes.func.isRequired,
  fetchCrmObjectTypes: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  fieldNamePath: PropTypes.string,
  readOnly: PropTypes.bool,
  selectedCrmObjectType: PropTypes.string
};
export default connect(makeMapStateToProps, mapDispatchToProps)(CrmObjectContentOptions);